import React from "react";
import { AlertTriangle, FolderOpen, Loader2 } from "lucide-react";
import { Button } from "./button";

/**
 * Loading, error and empty states — brand bible §44.
 *
 * DENSITY, AND WHO OWNS PADDING.
 *
 * `.vz-state-container` in App.css declares `padding: 48px 24px; margin: 14px 0`
 * and 30 call sites wrap the result in a second padded div — `py-12` at 22 of
 * them, `py-16`, `py-8`, `p-8` at the rest. An empty stock tab therefore drew
 * 48px of wrapper padding around 48px of container padding: 96px of blank space
 * above the words "No movements", on a screen whose whole job is density (§2.2).
 * Nobody chose 96px. It is what two components each choosing 48px adds up to.
 *
 * So padding is now a single decision made in one place, taken by the `density`
 * prop and enforced by attribute selectors in index.css that outrank App.css on
 * specificity. Call sites drop their wrapper div entirely:
 *
 *   before:  <div className="py-12"><EmptyState title="No movements" /></div>
 *   after:   <EmptyState title="No movements" />
 *
 *   inline — a state inside a table body, a card, or a small panel
 *   block  — a state that owns a whole panel or tab (the default)
 *
 * `block` is the default, so every existing call site drops to 32px of owned
 * padding immediately rather than waiting for a migration to reach it. That is
 * intentional and it is visible: until a call site deletes its wrapper it reads
 * 48 + 32 instead of 48 + 48, which is still too much but is never worse than
 * today. The wrapper deletions finish the job.
 */

/** Icon sizes are part of density: a 48px glyph inside a table row is a poster. */
const ICON = { inline: 20, block: 28 };

/**
 * A density must be one of the two names, because the CSS is attribute-driven.
 * `data-density="blok"` still matches `.vz-state-container[data-density]`, which
 * zeroes App.css's padding, and then matches neither named rule — leaving a
 * state with no padding at all. A typo has to fall back, not fall through.
 */
const resolveDensity = density => (density in ICON ? density : "block");

/**
 * One title weight, not two.
 *
 * ErrorState was `text-lg font-semibold` and EmptyState was `text-lg
 * font-medium` — the same rank in the same component file rendered at two
 * different weights depending on which state the server happened to return, so
 * a page flickered between hierarchies as it loaded. §4.2: do not randomly
 * change weights.
 */
const STATE_TITLE = "text-lede font-semibold";
const STATE_MESSAGE = "text-body text-muted-foreground max-w-md text-center";

export function LoadingState({
  message = "Loading...",
  fullScreen = false,
  density = "block",
  testId = "loading-state",
}) {
  const tone = resolveDensity(density);
  const content = (
    <div className="vz-state-container vz-loading" data-density={tone} data-testid={testId}>
      <Loader2 className="animate-spin text-muted-foreground" size={ICON[tone]} />
      <p className={`${STATE_MESSAGE} mt-snug`}>{message}</p>
    </div>
  );

  if (fullScreen) {
    return <div className="vz-state-fullscreen">{content}</div>;
  }
  return content;
}

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
  density = "block",
  testId = "error-state",
}) {
  const tone = resolveDensity(density);
  return (
    <div className="vz-state-container vz-error" data-density={tone} data-testid={testId}>
      <AlertTriangle className="text-destructive mb-snug" size={ICON[tone]} />
      <h3 className={STATE_TITLE}>{title}</h3>
      {message && <p className={`${STATE_MESSAGE} mt-tight`}>{message}</p>}
      {onRetry && (
        <Button variant="outline" className="mt-base" onClick={onRetry} data-testid="error-retry-button">
          Try Again
        </Button>
      )}
    </div>
  );
}

export function EmptyState({
  title = "No Data",
  message,
  icon: Icon = FolderOpen,
  action,
  density = "block",
  testId = "empty-state",
}) {
  const tone = resolveDensity(density);
  return (
    <div className="vz-state-container vz-empty" data-density={tone} data-testid={testId}>
      <Icon className="text-muted-foreground mb-snug opacity-50" size={ICON[tone]} />
      <h3 className={STATE_TITLE}>{title}</h3>
      {message && <p className={`${STATE_MESSAGE} mt-tight`}>{message}</p>}
      {action && <div className="mt-base">{action}</div>}
    </div>
  );
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          title="Unexpected Error"
          message={this.state.error?.message || "A component failed to render."}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}
